import { type ChangeEvent, useRef, useState } from "react";
import {
  useBulkCommitV1BrandsBulkUploadIdCommitPost,
  useBulkStatusV1BrandsBulkUploadIdGet,
  useBulkUploadV1BrandsBulkUploadPost,
  useBulkVerifyV1BrandsBulkUploadIdVerifyPost,
} from "../api/generated/qcsControlPlane";
import { Flow, type BulkUploadOut } from "../api/generated/model";
import { platformLabel } from "../api/labels";
import { platformKeysFor } from "../api/platforms";
import { Button, Panel, Row, Stack } from "@akaike/ui";
import { useToast } from "./toast";

// The template teaches the file's two shapes: one brand with a candidate on EVERY
// available platform (the common case -- the same brand is swept everywhere), then a
// second brand with a single-platform candidate and no aliases.
function csvTemplate(): string {
  const platforms = platformKeysFor(Flow.brand_sweep);
  const rows = [
    ...platforms.map((p, i) => `The Moms Co,${p},moms co,${i === 0 ? "The Moms Co|MomsCo" : ""},true`),
    ...platforms.slice(0, 1).map((p) => `Mamaearth,${p},mamaearth,,true`),
  ];
  return ["brand,platform,search_term,match_aliases,enabled", ...rows].join("\n") + "\n";
}

/** The 3-step bulk-import wizard: upload & parse -> verify all -> review & commit. */
export function BulkImport({
  onClose,
  onCommitted,
}: {
  onClose: () => void;
  onCommitted: () => void;
}) {
  const notify = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [upload, setUpload] = useState<BulkUploadOut | null>(null);
  const [includeUnverified, setIncludeUnverified] = useState(false);

  const uploadId = upload?.upload_id ?? null;

  // Poll while verification runs server-side; the query data is the source of
  // truth once started (react-query v5 dropped onSuccess), merged below.
  const poll = useBulkStatusV1BrandsBulkUploadIdGet(uploadId ?? "", {
    query: {
      enabled: !!uploadId,
      refetchInterval: (q) => (q.state.data?.status === "verifying" ? 1500 : false),
    },
  });
  const view: BulkUploadOut | null = poll.data ?? upload;
  const verifying = view?.status === "verifying";

  const doUpload = useBulkUploadV1BrandsBulkUploadPost({
    mutation: {
      onSuccess: (d) => setUpload(d),
      onError: (e) => notify(e instanceof Error ? e.message : "upload failed", "crit"),
    },
  });

  const startVerify = useBulkVerifyV1BrandsBulkUploadIdVerifyPost({
    mutation: {
      onSuccess: (d) => {
        setUpload(d);
        void poll.refetch(); // flip the poll into its 1.5s verifying cadence
      },
      onError: (e) => notify(e instanceof Error ? e.message : "verify failed", "crit"),
    },
  });

  const commit = useBulkCommitV1BrandsBulkUploadIdCommitPost({
    mutation: {
      onSuccess: (r) => {
        notify(`${r.brands_added} brands · ${r.candidates_added} candidates registered`, "ok");
        onCommitted();
      },
      onError: (e) => notify(e instanceof Error ? e.message : "commit failed", "crit"),
    },
  });

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    e.currentTarget.value = "";
    if (file) doUpload.mutate({ data: { file } });
  };

  const downloadTemplate = () => {
    const blob = new Blob([csvTemplate()], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "brands-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const rows = view?.rows ?? [];
  const errors = view?.errors ?? [];
  const verdicts = view?.verdicts ?? null;
  const progress = view?.progress;
  const verified = view?.status === "verified";
  const foundCount = verdicts?.filter((v) => v?.found).length ?? 0;

  return (
    <Modal title="Bulk import brands (CSV)" onClose={onClose}>
      {/* STEP 1: upload & parse */}
      {!upload && (
        <Stack gap="md">
          <p className="faint" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
            CSV columns: <code className="mono">brand, platform, search_term, match_aliases, enabled</code>.
            Aliases are <code className="mono">|</code>-separated. A <code className="mono">brand</code>-only
            CSV expands to one default candidate per platform.
          </p>
          <Row gap="sm">
            <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={onFile} />
            <Button
              variant="primary"
              loading={doUpload.isPending}
              onClick={() => fileRef.current?.click()}
            >
              {doUpload.isPending ? "Parsing…" : "Choose CSV…"}
            </Button>
            <Button variant="outline" onClick={downloadTemplate}>
              Download template
            </Button>
          </Row>
        </Stack>
      )}

      {/* STEP 1 result + STEP 2: verify */}
      {view && !verified && (
        <Stack gap="md">
          <Row gap="md">
            <span className="num">{rows.length} candidates parsed</span>
            {errors.length > 0 && (
              <span style={{ color: "var(--warn)", fontSize: 12 }}>
                {errors.length} row issue{errors.length === 1 ? "" : "s"}
              </span>
            )}
          </Row>

          {errors.length > 0 && (
            <Panel style={{ padding: 8, maxHeight: 110, overflow: "auto" }}>
              {errors.map((er) => (
                <div key={er.line} style={{ fontSize: 11.5, color: "var(--ink-2)" }}>
                  line {er.line}: {er.reason}
                </div>
              ))}
            </Panel>
          )}

          {verifying && progress && (
            <Stack gap="sm">
              <Row justify="between" style={{ fontSize: 12 }}>
                <span>
                  Verifying… {progress.done}/{progress.total}
                </span>
                <span className="faint">
                  {progress.found} found · {progress.missed} missed
                </span>
              </Row>
              <div
                style={{
                  height: 6,
                  background: "var(--bg-2)",
                  borderRadius: 3,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: "100%",
                    background: "var(--brand)",
                    borderRadius: 3,
                    transformOrigin: "left",
                    transform: `scaleX(${progress.total ? progress.done / progress.total : 0})`,
                    transition: "transform .3s",
                  }}
                />
              </div>
            </Stack>
          )}

          <BulkRowTable rows={rows} verdicts={verdicts} />

          {!verifying && (
            <Row justify="end">
              <Button
                variant="primary"
                loading={startVerify.isPending}
                disabled={rows.length === 0}
                onClick={() => {
                  if (uploadId) startVerify.mutate({ uploadId });
                }}
              >
                Verify all {rows.length} →
              </Button>
            </Row>
          )}
        </Stack>
      )}

      {/* STEP 3: review & commit */}
      {verified && (
        <Stack gap="md">
          <Row gap="md">
            <span style={{ color: "var(--ok)" }}>✓ {foundCount} found</span>
            <span className="faint">{rows.length - foundCount} not found</span>
          </Row>
          <BulkRowTable rows={rows} verdicts={verdicts} />
          <label className="row" style={{ gap: 8, fontSize: 12.5, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={includeUnverified}
              onChange={(e) => setIncludeUnverified(e.currentTarget.checked)}
            />
            Also register not-found candidates (disabled, to fix later)
          </label>
          <Row gap="sm" justify="end">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={commit.isPending}
              disabled={foundCount === 0 && !includeUnverified}
              onClick={() => {
                if (uploadId)
                  commit.mutate({
                    uploadId,
                    data: { include_unverified: includeUnverified },
                  });
              }}
            >
              {commit.isPending
                ? "Registering…"
                : `Register ${includeUnverified ? rows.length : foundCount}`}
            </Button>
          </Row>
        </Stack>
      )}
    </Modal>
  );
}

function BulkRowTable({
  rows,
  verdicts,
}: {
  rows: { brand: string; platform: string; search_term: string }[];
  verdicts: BulkUploadOut["verdicts"];
}) {
  return (
    <Panel style={{ maxHeight: 280, overflow: "auto" }}>
      <table className="tbl">
        <thead>
          <tr>
            <th>Brand</th>
            <th>Platform</th>
            <th>Term</th>
            <th className="c">Result</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const v = verdicts?.[i];
            return (
              <tr key={`${r.brand}-${r.platform}-${r.search_term}-${i}`}>
                <td className="strong">{r.brand}</td>
                <td>{platformLabel(r.platform)}</td>
                <td className="mono" style={{ fontSize: 11.5 }}>
                  {r.search_term}
                </td>
                <td className="c">
                  {!v ? (
                    <span className="faint">—</span>
                  ) : v.found ? (
                    <span style={{ color: "var(--ok)" }}>✓ {v.hit_count}</span>
                  ) : (
                    <span style={{ color: "var(--warn)" }}>✗</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Panel>
  );
}

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 40,
        background: "rgba(0,0,0,.45)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "48px 16px",
      }}
      onClick={onClose}
    >
      <Panel
        style={{ width: "100%", maxWidth: 640 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Panel.Head title={title}>
          <div className="flex-1" />
          <Button variant="outline" size="sm" icon onClick={onClose} aria-label="close">
            ✕
          </Button>
        </Panel.Head>
        <Panel.Body>{children}</Panel.Body>
      </Panel>
    </div>
  );
}
